import { DISABLE_AUTOMATIC_WRAPPING, WidgetComponent } from "../widget.component";
import { EditableWidget, WIDGET_JITSI } from "@shared/models/widget";
import { Component, ChangeDetectionStrategy } from "@angular/core";
import { FE_JitsiWidget } from "@shared/models/integration";
import { SessionStorage } from "@shared/SessionStorage";
import { NameService } from "@shared/services/name.service";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { LogService } from "@shared/services/log.service";
import { ConfigScreenWidgetComponent } from "../config-screen/config-screen.widget.component";
import { FieldComponent } from "../../../elements/field/field.component";

@Component({
    templateUrl: "jitsi.widget.component.html",
    styleUrls: ["jitsi.widget.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ConfigScreenWidgetComponent, FieldComponent, TranslatePipe]
})
export class JitsiWidgetConfigComponent extends WidgetComponent {

    private readonly jitsiWidget: FE_JitsiWidget = <FE_JitsiWidget>SessionStorage.editIntegration;

    constructor(private readonly nameService: NameService, public translate: TranslateService, logService: LogService) {
        super(WIDGET_JITSI, "Jitsi Video Conference", translate, logService, DISABLE_AUTOMATIC_WRAPPING);
    }

    protected OnWidgetsDiscovered(widgets: EditableWidget[]) {
        for (const widget of widgets) {
            this.processDiscoveredWidget(widget);
        }
    }

    private processDiscoveredWidget(widget: EditableWidget): void {
        let searchParams: URLSearchParams;
        try {
            searchParams = new URL(this.templateUrl(widget.url, widget.data)).searchParams;
        } catch {
            return;
        }

        widget.data.conferenceId = searchParams.get("confId") || searchParams.get("conferenceId");
        widget.data.domain = searchParams.get("domain") || "jitsi.riot.im";

        const audioConf = this.parseAudioFlag(searchParams.get("isAudioConf"), widget.data["isAudioConf"]);
        const audioOnly = this.parseAudioFlag(searchParams.get("isAudioOnly"), widget.data["isAudioOnly"]);
        widget.data.isAudioOnly = audioConf === null ? !!audioOnly : !!audioConf;

        widget.data.conferenceUrl = "https://" + widget.data.domain + "/" + widget.data.conferenceId;
    }

    private parseAudioFlag(value: string, dataFallback: unknown): boolean | null {
        if (value === "true") return true;
        if (value === "false") return false;
        if (value?.startsWith('$')) return typeof dataFallback === "boolean" ? dataFallback : null;
        return null;
    }

    protected OnNewWidgetPrepared(widget: EditableWidget): void {
        const name = this.nameService.getHumanReadableName();

        let rootUrl = "https://jitsi.riot.im/";
        if (this.jitsiWidget.options?.jitsiDomain) {
            rootUrl = "https://" + this.jitsiWidget.options.jitsiDomain + "/";
        }

        widget.dimension.newData["conferenceUrl"] = rootUrl + name;
    }

    protected OnWidgetBeforeAdd(widget: EditableWidget) {
        this.setJitsiUrl(widget);
    }

    protected OnWidgetBeforeEdit(widget: EditableWidget) {
        this.setJitsiUrl(widget);
    }

    private setJitsiUrl(widget: EditableWidget) {
        let jitsiUrl: URL;
        try {
            jitsiUrl = new URL(widget.dimension.newData.conferenceUrl);
        } catch {
            throw new Error("Please enter a valid conference URL (e.g. https://meet.jit.si/room)");
        }
        widget.dimension.newData.domain = jitsiUrl.host;
        widget.dimension.newData.conferenceId = jitsiUrl.pathname.substring(1);

        let widgetQueryString = "?" + new URLSearchParams({
            "conferenceId": "$conferenceId",
            "domain": "$domain",
            "isAudioOnly": "$isAudioOnly",
            "displayName": "$matrix_display_name",
            "avatarUrl": "$matrix_avatar_url",
            "userId": "$matrix_user_id",
        }).toString();
        widgetQueryString = this.decodeParams(widgetQueryString, Object.keys(widget.dimension.newData).map(k => "$" + k));

        widget.dimension.newUrl = window.location.origin + "/widgets/jitsi" + widgetQueryString;
    }
}
